import { cn } from "@/lib/utils";
import { ReactNode, useMemo } from "react";

import ArrowButton from "../ArrowButton/ArrowButton";

const PAGES_TO_SHOW = 5;

type Slot = number | "gap-start" | "gap-end";

/**
 * A window of pages around the current one. When pages fall outside it, the
 * first and last page stay reachable behind a gap.
 */
const pageSlots = (currentPage: number, totalPages: number): Slot[] => {
  let start = 1;
  let end = totalPages;
  if (totalPages > PAGES_TO_SHOW) {
    const half = Math.floor(PAGES_TO_SHOW / 2);
    if (currentPage <= half + 1) end = PAGES_TO_SHOW;
    else if (currentPage >= totalPages - half)
      start = totalPages - PAGES_TO_SHOW + 1;
    else {
      start = currentPage - half;
      end = currentPage + half;
    }
  }
  const slots: Slot[] = [];
  if (start > 1) slots.push(1);
  if (start > 2) slots.push("gap-start");
  for (let p = start; p <= end; p++) slots.push(p);
  if (end < totalPages - 1) slots.push("gap-end");
  if (end < totalPages) slots.push(totalPages);
  return slots;
};

const PageButton = ({
  page,
  current,
  onClick,
}: {
  page: number;
  current: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-current={current ? "page" : undefined}
    className={cn(
      "grid h-7 min-w-7 cursor-pointer place-items-center rounded-full border px-1.5 text-xs font-semibold transition-colors",
      current
        ? "border-edge-strongest bg-control text-foreground"
        : "border-edge-strong bg-surface text-foreground-muted hover:bg-control hover:text-foreground",
    )}
  >
    {page}
  </button>
);

/** Round page buttons between arrows, with an optional caption above. */
export const Pagination = ({
  currentPage,
  totalPages,
  onChange,
  caption,
  className,
}: {
  currentPage: number;
  totalPages: number;
  onChange: (page: number) => void;
  /** e.g. "Showing 1-5 of 16 chains". */
  caption?: ReactNode;
  className?: string;
}) => {
  const slots = useMemo(
    () => pageSlots(currentPage, totalPages),
    [currentPage, totalPages],
  );
  if (totalPages <= 1) return null;

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-3",
        className,
      )}
    >
      {caption && (
        <div className="text-xs text-foreground-muted">{caption}</div>
      )}
      <div className="flex flex-row items-center justify-center gap-2">
        <ArrowButton
          direction="left"
          disabled={currentPage === 1}
          onClick={() => onChange(Math.max(1, currentPage - 1))}
        />
        {slots.map((slot) =>
          typeof slot === "number" ? (
            <PageButton
              key={slot}
              page={slot}
              current={slot === currentPage}
              onClick={() => onChange(slot)}
            />
          ) : (
            <span
              key={slot}
              className="w-4 text-center text-xs text-foreground-muted"
            >
              …
            </span>
          ),
        )}
        <ArrowButton
          direction="right"
          disabled={currentPage === totalPages}
          onClick={() => onChange(Math.min(totalPages, currentPage + 1))}
        />
      </div>
    </div>
  );
};

export default Pagination;
